import { cn } from '@repo/tailwind-config/tailwind.functions'
import * as React from 'react'

import { typography } from '../../atoms/typography'

type TableProps = React.HTMLAttributes<HTMLTableElement> & {
  borderRadius?: number
  containerClassName?: string
}

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ className, borderRadius, containerClassName, ...props }, ref) => (
    <div
      className={cn(
        'relative w-full overflow-auto rounded-3xl bg-gray-50 border border-primary-light',
        containerClassName
      )}
      style={{ borderRadius }}
    >
      <table ref={ref} className={cn('w-full caption-bottom text-sm', className)} {...props} />
    </div>
  )
)
Table.displayName = 'Table'

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => <thead ref={ref} className={cn(className)} {...props} />)
TableHeader.displayName = 'TableHeader'

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn('[&_tr:last-child]:border-0', className)} {...props} />
))
TableBody.displayName = 'TableBody'

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot ref={ref} className={cn('bg-muted/50 border-t font-medium', className)} {...props} />
))
TableFooter.displayName = 'TableFooter'

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => {
    const childrenArray = React.Children.toArray(props.children)

    return (
      <tr
        ref={ref}
        className={cn(
          'hover:bg-muted/50 data-[state=selected]:bg-muted transition-colors border-b border-white-active h-16 text-sm font-light',
          className
        )}
        {...props}
      >
        {childrenArray.map((child, index) =>
          React.cloneElement(child as React.ReactElement, {
            key: index,
            isfirst: `${index === 0}`,
            islast: `${index === childrenArray.length - 1}`
          })
        )}
      </tr>
    )
  }
)
TableRow.displayName = 'TableRow'

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      'text-black bg-primary-light pl-6 pr-3 text-left align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
      className
    )}
    style={typography['secondary-body']}
    {...props}
  />
))
TableHead.displayName = 'TableHead'

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement> & {
    isfirst?: 'true' | 'false'
    islast?: 'true' | 'false'
  }
>(({ className, children, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      'bg-white pl-6 pr-3 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
      className
    )}
    {...props}
  >
    {children}
  </td>
))
TableCell.displayName = 'TableCell'

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption ref={ref} className={cn('text-muted-foreground mt-4 text-sm', className)} {...props} />
))
TableCaption.displayName = 'TableCaption'

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption }
